const toNumber = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const pickString = (...values) => {
  for (const value of values) {
    const trimmed = String(value ?? '').trim()
    if (trimmed) return trimmed
  }
  return ''
}

const pickNumber = (...values) => {
  for (const value of values) {
    const parsed = Number(value)
    if (Number.isFinite(parsed) && parsed > 0) return parsed
  }
  return 0
}

const pickBoolean = value => {
  if (typeof value === 'boolean') return value
  if (value === 1 || value === '1' || value === 'true') return true
  if (value === 0 || value === '0' || value === 'false') return false
  return Boolean(value)
}

const toInitials = text => {
  const parts = String(text || '')
    .split(' ')
    .map(part => part.trim())
    .filter(Boolean)

  if (!parts.length) return 'N'

  return parts
    .slice(0, 2)
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
}

export const ADMIN_NOTIFICATIONS_UPDATED = 'admin-notifications-updated'

export const notificationReadFilterOptions = [
  { value: 'all', text: 'All notifications' },
  { value: 'false', text: 'Unread' },
  { value: 'true', text: 'Read' },
]

const resolveNotificationImage = (item, data) => {
  const raw = pickString(
    item?.image,
    item?.image_url,
    item?.avatar,
    item?.avatar_url,
    item?.logo,
    item?.company_logo,
    item?.profile_image,
    data?.image,
    data?.image_url,
    data?.avatar,
    data?.avatar_url,
    data?.logo,
    data?.company_logo,
    data?.profile_image,
  )

  if (!raw) return ''

  if (/^https?:\/\//i.test(raw)) return raw

  const base = String(process.env.VUE_APP_IMAGE_PATH || '').replace(/\/+$/, '')
  if (!base) return raw

  return `${base}/${raw.replace(/^\/+/, '')}`
}

const extractNotificationItems = responseData => {
  const body = responseData && typeof responseData === 'object' ? responseData : {}
  const candidates = [
    body,
    body.data,
    body.notifications,
    body.data?.data,
    body.data?.notifications,
    body.data?.items,
    body.items,
  ]

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return {
        items: candidate,
        pagination: body.pagination || body.data?.pagination || {},
        meta: body.meta || body.data?.meta || {},
      }
    }
  }

  return {
    items: [],
    pagination: body.pagination || body.data?.pagination || {},
    meta: body.meta || body.data?.meta || {},
  }
}

export const normalizeNotification = item => {
  const data = item?.data && typeof item.data === 'object' ? item.data : {}
  const title = pickString(item?.title, 'Notification')
  const message = pickString(item?.message, item?.body, item?.description)

  return {
    id: toNumber(item?.id),
    title,
    message,
    image: resolveNotificationImage(item, data),
    type: pickString(item?.type, item?.notification_type, data?.notification_type, data?.type, 'system'),
    isRead: pickBoolean(item?.is_read ?? item?.isRead ?? item?.read),
    timeAgo: pickString(item?.time_ago, item?.timeAgo),
    createdAt: pickString(item?.created_at, item?.createdAt),
    initials: toInitials(title),
    data,
    redirectType: pickString(item?.redirect_type, item?.redirectType, data?.redirect_type, data?.redirectType),
    adId: pickNumber(item?.ad_id, item?.adId, data?.ad_id, data?.adId),
    vendorId: pickNumber(item?.vendor_id, item?.vendorId, data?.vendor_id, data?.vendorId),
    companyId: pickNumber(item?.company_id, item?.companyId, data?.company_id, data?.companyId),
  }
}

export const resolveAdminNotificationRoute = item => {
  if (!item) return null

  const redirect = item.redirectType || item.data?.redirect_type || ''
  const adId = item.adId || item.data?.ad_id
  const vendorId = item.vendorId || item.data?.vendor_id

  if (redirect === 'ad' && adId) {
    return { name: 'ads-pending' }
  }

  if ((redirect === 'company' || redirect === 'payment') && vendorId) {
    return { name: 'advertiser-overall-details', params: { id: String(vendorId) } }
  }

  if (redirect === 'company') {
    return { name: 'advertisers-management' }
  }

  return null
}

export const normalizeNotificationsResponse = responseData => {
  const { items, pagination, meta } = extractNotificationItems(responseData)

  return {
    notifications: items.map(normalizeNotification),
    unreadCount: toNumber(meta.unread_count ?? meta.unreadCount),
    pagination: {
      currentPage: toNumber(pagination.current_page ?? pagination.currentPage) || 1,
      totalPages: toNumber(pagination.last_page ?? pagination.total_pages ?? pagination.lastPage),
      totalRecords: toNumber(pagination.total ?? pagination.total_records ?? pagination.totalRecords),
      perPage: toNumber(pagination.per_page ?? pagination.perPage) || 10,
    },
  }
}
